import { reactive } from 'vue';
import { BinaryFilesByIdsDocument } from '@ohmybible/graphql';
import type { useApolloClient } from '@vue/apollo-composable';

type ApolloClientLike = ReturnType<typeof useApolloClient>['client'];

export interface HydratedBinaryFile {
  status: 'pending' | 'ready' | 'error';
  url?: string;
  downloadUrl?: string;
  /** When the URLs were signed, by the wall clock — see `RENEW_AFTER_MS`. */
  fetchedAt?: number;
  /** When the last error-driven refetch ran — see `refreshBinaryFileUrls`. */
  refreshedAt?: number;
  /** When the last lookup for this id failed — see `resolveBinaryFileUrls`. */
  failedAt?: number;
}

/**
 * Signed URLs live for an hour, so a URL that fails within minutes of being
 * refetched is not expired — the file itself doesn't render (e.g. HEIC).
 */
const REFRESH_COOLDOWN_MS = 5 * 60 * 1000;

/**
 * Signed URLs live for an hour (`SIGNED_URL_TTL` in the API) and a support
 * editor keeps a page open all day. An image announces an expired URL by
 * failing to load; a file card and the lightbox's download announce nothing —
 * the click just lands on a 403. So a URL older than this is renewed before it
 * is handed out, and in the background for as long as the tab lives.
 *
 * Measured on the wall clock rather than by one timer per URL: a laptop that
 * slept through the hour ran no timers, and wakes up with every URL dead.
 */
const RENEW_AFTER_MS = 45 * 60 * 1000;

/** How often the background sweep looks for URLs past `RENEW_AFTER_MS`. */
const SWEEP_INTERVAL_MS = 60 * 1000;

/**
 * A lookup that failed is not a verdict for the rest of the session: the
 * network blinks, and a colleague's file becomes readable the moment their
 * text is saved. The next component that asks gets a fresh attempt — but not
 * more often than this, or a file that really is gone would be asked about on
 * every render.
 */
const FAILED_RETRY_MS = 30 * 1000;

/** Pauses before re-sending a lookup whose REQUEST failed; after the last one the card stays failed. */
const REQUEST_RETRY_DELAYS_MS = [3_000, 10_000, 30_000];

/** At most this many ids per `binaryFilesByIds` call, so one request stays a sane size. */
const LOOKUP_BATCH = 100;

/**
 * Presigned URLs must never be persisted in RichText payloads (they expire in
 * ~1h) — attachment nodes store only the BinaryFile id and resolve fresh URLs
 * here at render time. Requests are microtask-batched: everything asked for in
 * one tick goes out together, in calls of at most `LOOKUP_BATCH` ids, so a
 * document full of attachments normally costs one round-trip.
 */
const cache = reactive(new Map<string, HydratedBinaryFile>());

let pendingIds = new Set<string>();
let flushScheduled = false;

/** Lookups on the wire, by id: a renewal is not queued twice, and a click waits for the one already sent. */
const inFlight = new Map<string, Promise<void>>();

const isStale = (entry: HydratedBinaryFile): boolean =>
  entry.status === 'ready' && Date.now() - (entry.fetchedAt ?? 0) >= RENEW_AFTER_MS;

const queueRenewal = (id: string): void => {
  if (!inFlight.has(id)) {
    pendingIds.add(id);
  }
};

const markFailed = (id: string): void => {
  const entry = cache.get(id);

  if (entry) {
    entry.status = 'error';
    entry.failedAt = Date.now();
  }
};

const fetchUrls = async (client: ApolloClientLike, ids: string[]): Promise<void> => {
  try {
    const { data } = await client.query({
      query: BinaryFilesByIdsDocument,
      variables: { ids },
      fetchPolicy: 'network-only',
    });

    const found = new Set<string>();
    const fetchedAt = Date.now();

    for (const file of data?.binaryFilesByIds ?? []) {
      found.add(file.id);
      retryAttempts.delete(file.id);
      // Keep the refetch time: fresh URLs alone don't prove the file renders
      // (e.g. HEIC, which browsers can't decode), and dropping it here turned
      // every load error into an endless refetch → re-render → error loop.
      cache.set(file.id, {
        status: 'ready',
        url: file.url ?? undefined,
        downloadUrl: file.downloadUrl ?? undefined,
        fetchedAt,
        refreshedAt: cache.get(file.id)?.refreshedAt,
      });
    }

    for (const id of ids) {
      if (!found.has(id)) {
        markFailed(id);
      }
    }
  } catch {
    // A failed RENEWAL leaves a ready entry as it is — its URL may well still
    // be valid — and the next sweep tries again.
    const failed = ids.filter((id) => cache.get(id)?.status !== 'ready');

    failed.forEach(markFailed);
    scheduleRetry(client, failed);
  }
};

const lookup = (client: ApolloClientLike, ids: string[]): Promise<void> => {
  const request = fetchUrls(client, ids);

  ids.forEach((id) => inFlight.set(id, request));
  void request.finally(() => ids.forEach((id) => {
    if (inFlight.get(id) === request) {
      inFlight.delete(id);
    }
  }));

  return request;
};

/**
 * The REQUEST failed — the network blinked, the API was restarting — which
 * says nothing about the files. Nothing on screen will ask again by itself: a
 * mounted node holds its entry in a computed that only this cache can
 * invalidate. So the cache asks again, a few times with growing pauses, and
 * the cards recover without a reload. An id the server ANSWERED without is a
 * different case and is not retried here — see `resolveBinaryFileUrls`.
 */
const retryAttempts = new Map<string, number>();

const scheduleRetry = (client: ApolloClientLike, ids: string[]): void => {
  const byDelay = new Map<number, string[]>();

  for (const id of ids) {
    const attempt = retryAttempts.get(id) ?? 0;
    const delay = REQUEST_RETRY_DELAYS_MS[attempt];

    if (delay === undefined) {
      continue;
    }

    retryAttempts.set(id, attempt + 1);
    byDelay.set(delay, [...(byDelay.get(delay) ?? []), id]);
  }

  for (const [delay, batch] of byDelay) {
    setTimeout(() => {
      for (const id of batch) {
        const entry = cache.get(id);

        // Still failed and nobody re-queued it meanwhile.
        if (entry?.status === 'error') {
          entry.status = 'pending';
          entry.failedAt = Date.now();
          pendingIds.add(id);
        }
      }

      scheduleFlush(client);
    }, delay);
  }
};

const flush = async (client: ApolloClientLike): Promise<void> => {
  const ids = [...pendingIds];
  pendingIds = new Set();
  flushScheduled = false;

  const batches: string[][] = [];

  for (let index = 0; index < ids.length; index += LOOKUP_BATCH) {
    batches.push(ids.slice(index, index + LOOKUP_BATCH));
  }

  await Promise.all(batches.map((batch) => lookup(client, batch)));
};

const scheduleFlush = (client: ApolloClientLike): void => {
  if (flushScheduled || pendingIds.size === 0) {
    return;
  }

  flushScheduled = true;
  queueMicrotask(() => void flush(client));
};

/**
 * How many mounted views show each file. Only those are renewed in the
 * background: the cache outlives every page, and a file nothing shows any more
 * is renewed on its next use instead (`resolveBinaryFileUrls`,
 * `ensureFreshBinaryFileUrls`).
 */
const holders = new Map<string, number>();

/** A view showing `fileId` keeps its URLs renewed while it holds it; call the result to let go. */
export const holdBinaryFileUrls = (fileId: string): (() => void) => {
  holders.set(fileId, (holders.get(fileId) ?? 0) + 1);

  let released = false;

  return () => {
    if (released) {
      return;
    }

    released = true;

    const count = (holders.get(fileId) ?? 1) - 1;

    if (count > 0) {
      holders.set(fileId, count);
    } else {
      holders.delete(fileId);
    }
  };
};

/**
 * Quietly: a stale entry stays `ready` with its current URL until the new one
 * arrives, so nothing on screen blinks.
 */
const renewStale = (client: ApolloClientLike): void => {
  for (const id of holders.keys()) {
    const entry = cache.get(id);

    if (entry && isStale(entry)) {
      queueRenewal(id);
    }
  }

  scheduleFlush(client);
};

let sweepStarted = false;

/**
 * One sweep for the whole tab, started by the first lookup, over the files on
 * screen: every minute, and the moment the tab is looked at again — after a
 * laptop wakes up, their URLs are renewed before anyone clicks them.
 */
const startSweep = (client: ApolloClientLike): void => {
  if (sweepStarted || typeof window === 'undefined') {
    return;
  }

  sweepStarted = true;

  window.setInterval(() => renewStale(client), SWEEP_INTERVAL_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      renewStale(client);
    }
  });
};

/**
 * Returns the (reactive) cache entry for a file id, queueing a batched fetch
 * on first access — again when the previous lookup failed long enough ago
 * (`FAILED_RETRY_MS`), and quietly when its URLs are past `RENEW_AFTER_MS`.
 * Components read `.status` / `.url` / `.downloadUrl`.
 */
export const resolveBinaryFileUrls = (client: ApolloClientLike, fileId: string): HydratedBinaryFile => {
  startSweep(client);

  const existing = cache.get(fileId);

  if (existing) {
    if (
      existing.status === 'error'
      && existing.failedAt !== undefined
      && Date.now() - existing.failedAt >= FAILED_RETRY_MS
    ) {
      // `failedAt` moves first, so the re-renders this very retry causes do
      // not queue it again.
      existing.failedAt = Date.now();
      existing.status = 'pending';
      pendingIds.add(fileId);
      scheduleFlush(client);
    } else if (isStale(existing)) {
      queueRenewal(fileId);
      scheduleFlush(client);
    }

    return existing;
  }

  const entry: HydratedBinaryFile = { status: 'pending' };
  cache.set(fileId, entry);
  pendingIds.add(fileId);
  scheduleFlush(client);

  return entry;
};

/**
 * The entry for `fileId` with URLs fit to hand to the browser right now —
 * renewed first when they are past `RENEW_AFTER_MS`. For a click (a download,
 * the lightbox) whose stale URL would answer 403 instead of the file. When the
 * renewal itself fails, the entry comes back as it was.
 */
export const ensureFreshBinaryFileUrls = async (
  client: ApolloClientLike,
  fileId: string,
): Promise<HydratedBinaryFile | undefined> => {
  const entry = cache.get(fileId);

  if (entry?.status === 'ready' && !isStale(entry)) {
    return entry;
  }

  await (inFlight.get(fileId) ?? lookup(client, [fileId]));

  return cache.get(fileId);
};

/** The cache entry for `fileId` as it is — no lookup queued. */
export const peekBinaryFileUrls = (fileId: string): HydratedBinaryFile | undefined => cache.get(fileId);

/** A signed URL without its signature: the object it points at, the same across renewals. */
const objectAddress = (url: string): string => url.split(/[?#]/, 1)[0] ?? '';

/**
 * The file a URL this tab resolved belongs to. «Copy image» on an uploaded
 * picture puts its presigned `src` on the clipboard; the editor uses this to
 * turn the pasted copy back into the file it is (see `AttachmentImage`)
 * instead of a link that dies within the hour. Compared without the query, so
 * a copy taken before the cache renewed the URL still finds its file.
 */
export const findFileIdByUrl = (url: string): string | null => {
  const target = objectAddress(url);

  if (!/^https?:\/\//i.test(target)) {
    return null;
  }

  for (const [fileId, entry] of cache) {
    if ([entry.url, entry.downloadUrl].some((known) => known && objectAddress(known) === target)) {
      return fileId;
    }
  }

  return null;
};

/** Seed the cache from a `createBinaryFile` response — skips the initial fetch. */
export const primeBinaryFileUrls = (
  fileId: string,
  urls: { url?: string | null; downloadUrl?: string | null },
): void => {
  cache.set(fileId, {
    status: 'ready',
    url: urls.url ?? undefined,
    downloadUrl: urls.downloadUrl ?? undefined,
    fetchedAt: Date.now(),
  });
};

/**
 * Refetch a file's URLs after an image failed to load (presigned URL expired).
 * At most once per cooldown window, so an expiry always gets its refresh while
 * a file the browser can't decode stops after one. Returns false when refused.
 */
export const refreshBinaryFileUrls = (client: ApolloClientLike, fileId: string): boolean => {
  const entry = cache.get(fileId);

  if (!entry || (entry.refreshedAt !== undefined && Date.now() - entry.refreshedAt < REFRESH_COOLDOWN_MS)) {
    return false;
  }

  entry.refreshedAt = Date.now();
  entry.status = 'pending';
  pendingIds.add(fileId);
  scheduleFlush(client);

  return true;
};
